<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';

import Avatar from 'next/avatar/Avatar.vue';
import ReportHeader from './components/ReportHeader.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();

const agents = useMapGetter('agents/getAgents');

const isLoading = ref(false);
const isRefreshing = ref(false);
const hasLoaded = ref(false);
const lastUpdatedAt = ref(null);
const selectedStatus = ref('all');
const selectedAgentId = ref('all');

const STATUS_KEYS = ['online', 'busy', 'offline'];

const statusClasses = {
  online: 'bg-n-teal-3 text-n-teal-11',
  busy: 'bg-n-amber-3 text-n-amber-11',
  offline: 'bg-n-slate-3 text-n-slate-11',
};

const fetchAgents = async () => {
  isLoading.value = !hasLoaded.value;
  isRefreshing.value = hasLoaded.value;

  try {
    await store.dispatch('agents/get');
    lastUpdatedAt.value = new Date();
    hasLoaded.value = true;
  } catch {
    useAlert(t('ONLINE_USERS_REPORT.ERRORS.LOAD_FAILED'));
  } finally {
    isLoading.value = false;
    isRefreshing.value = false;
  }
};

const { startRefetching } = useLiveRefresh(fetchAgents, 20000);

const statusLabels = computed(() => ({
  online: t('ONLINE_USERS_REPORT.STATUS.ONLINE'),
  busy: t('ONLINE_USERS_REPORT.STATUS.BUSY'),
  offline: t('ONLINE_USERS_REPORT.STATUS.OFFLINE'),
}));

const statusOptions = computed(() => [
  {
    value: 'all',
    label: t('ONLINE_USERS_REPORT.FILTERS.ALL_STATUSES'),
  },
  ...STATUS_KEYS.map(status => ({
    value: status,
    label: statusLabels.value[status],
  })),
]);

const agentOptions = computed(() => [
  {
    value: 'all',
    label: t('ONLINE_USERS_REPORT.FILTERS.ALL_USERS'),
  },
  ...agents.value
    .map(agent => ({
      value: String(agent.id),
      label: agent.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)),
]);

const roleLabels = computed(() => ({
  agent: t('ONLINE_USERS_REPORT.ROLES.AGENT'),
  administrator: t('ONLINE_USERS_REPORT.ROLES.ADMINISTRATOR'),
}));

const statusCounts = computed(() =>
  STATUS_KEYS.reduce(
    (counts, status) => ({
      ...counts,
      [status]: agents.value.filter(
        agent => agent.availability_status === status
      ).length,
    }),
    {}
  )
);

const filteredUsers = computed(() => {
  const statusFilter = selectedStatus.value;
  const agentFilter = selectedAgentId.value;

  return agents.value
    .filter(agent => {
      const matchesStatus =
        statusFilter === 'all' || agent.availability_status === statusFilter;
      const matchesAgent =
        agentFilter === 'all' || String(agent.id) === agentFilter;

      return matchesStatus && matchesAgent;
    })
    .sort((a, b) => {
      const statusOrder =
        STATUS_KEYS.indexOf(a.availability_status) -
        STATUS_KEYS.indexOf(b.availability_status);

      return statusOrder || a.name.localeCompare(b.name);
    });
});

const formatDateTime = date => {
  if (!date) return t('ONLINE_USERS_REPORT.LAST_UPDATED_PENDING');

  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

const roleLabel = role => {
  return roleLabels.value[role] || roleLabels.value.agent;
};

const autoOfflineLabel = autoOffline =>
  autoOffline
    ? t('ONLINE_USERS_REPORT.AUTO_OFFLINE.ENABLED')
    : t('ONLINE_USERS_REPORT.AUTO_OFFLINE.DISABLED');

const statusLabel = status => statusLabels.value[status] || status;

const statusClass = status => statusClasses[status] || statusClasses.offline;

const escapeCsvValue = value => {
  const stringValue = String(value ?? '');
  if (!/[;"\n]/.test(stringValue)) return stringValue;

  return `"${stringValue.replace(/"/g, '""')}"`;
};

const downloadCSV = () => {
  const capturedAt = formatDateTime(lastUpdatedAt.value || new Date());
  const header = [
    t('ONLINE_USERS_REPORT.TABLE.USER'),
    t('ONLINE_USERS_REPORT.TABLE.EMAIL'),
    t('ONLINE_USERS_REPORT.TABLE.ROLE'),
    t('ONLINE_USERS_REPORT.TABLE.STATUS'),
    t('ONLINE_USERS_REPORT.TABLE.AUTO_OFFLINE'),
    t('ONLINE_USERS_REPORT.TABLE.CAPTURED_AT'),
  ];
  const rows = filteredUsers.value.map(agent => [
    agent.name,
    agent.email,
    roleLabel(agent.role),
    statusLabel(agent.availability_status),
    autoOfflineLabel(agent.auto_offline),
    capturedAt,
  ]);

  const csv = [header, ...rows]
    .map(row => row.map(escapeCsvValue).join(';'))
    .join('\n');

  downloadCsvFile('relatorio_status_usuarios_agora.csv', `\uFEFF${csv}`);
};

onMounted(() => {
  fetchAgents();
  startRefetching();
});
</script>

<template>
  <ReportHeader
    :header-title="$t('ONLINE_USERS_REPORT.TITLE')"
    :header-description="$t('ONLINE_USERS_REPORT.DESCRIPTION')"
  >
    <div class="flex items-center gap-2">
      <V4Button
        slate
        outline
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="$t('ONLINE_USERS_REPORT.REFRESH')"
        :is-loading="isRefreshing"
        :disabled="isLoading || isRefreshing"
        @click="fetchAgents"
      />
      <V4Button
        teal
        size="sm"
        icon="i-lucide-file-spreadsheet"
        :label="$t('ONLINE_USERS_REPORT.EXPORT_CSV')"
        :disabled="isLoading"
        @click="downloadCSV"
      />
    </div>
  </ReportHeader>

  <div class="flex flex-col gap-4 pb-6">
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <div class="rounded-lg border border-n-weak bg-n-surface-1 p-4">
        <span class="text-sm font-medium text-n-slate-11">
          {{ $t('ONLINE_USERS_REPORT.SUMMARY.ONLINE') }}
        </span>
        <p class="mb-0 mt-2 text-3xl font-semibold text-n-slate-12">
          {{ statusCounts.online || 0 }}
        </p>
      </div>
      <div class="rounded-lg border border-n-weak bg-n-surface-1 p-4">
        <span class="text-sm font-medium text-n-slate-11">
          {{ $t('ONLINE_USERS_REPORT.SUMMARY.BUSY') }}
        </span>
        <p class="mb-0 mt-2 text-3xl font-semibold text-n-slate-12">
          {{ statusCounts.busy || 0 }}
        </p>
      </div>
      <div class="rounded-lg border border-n-weak bg-n-surface-1 p-4">
        <span class="text-sm font-medium text-n-slate-11">
          {{ $t('ONLINE_USERS_REPORT.SUMMARY.OFFLINE') }}
        </span>
        <p class="mb-0 mt-2 text-3xl font-semibold text-n-slate-12">
          {{ statusCounts.offline || 0 }}
        </p>
      </div>
      <div class="rounded-lg border border-n-weak bg-n-surface-1 p-4">
        <span class="text-sm font-medium text-n-slate-11">
          {{ $t('ONLINE_USERS_REPORT.SUMMARY.TOTAL') }}
        </span>
        <p class="mb-0 mt-2 text-3xl font-semibold text-n-slate-12">
          {{ agents.length }}
        </p>
      </div>
    </div>

    <div
      class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-surface-1 p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            $t('ONLINE_USERS_REPORT.ONLINE_NOW', {
              count: filteredUsers.length,
            })
          }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{
            $t('ONLINE_USERS_REPORT.LAST_UPDATED', {
              time: formatDateTime(lastUpdatedAt),
            })
          }}
        </span>
      </div>
      <div class="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
        <select
          v-model="selectedStatus"
          class="h-9 min-w-40 rounded-lg border border-n-weak bg-n-surface-2 px-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-teal-8"
        >
          <option
            v-for="option in statusOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <select
          v-model="selectedAgentId"
          class="h-9 min-w-52 rounded-lg border border-n-weak bg-n-surface-2 px-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-teal-8"
        >
          <option
            v-for="option in agentOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>
    </div>

    <div class="overflow-x-auto rounded-lg border border-n-weak">
      <table class="w-full min-w-[720px] text-sm">
        <thead>
          <tr class="border-b border-n-weak bg-n-surface-2">
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('ONLINE_USERS_REPORT.TABLE.USER') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('ONLINE_USERS_REPORT.TABLE.EMAIL') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('ONLINE_USERS_REPORT.TABLE.ROLE') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('ONLINE_USERS_REPORT.TABLE.STATUS') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('ONLINE_USERS_REPORT.TABLE.AUTO_OFFLINE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="5" class="px-4 py-8 text-center text-n-slate-11">
              <span
                class="i-lucide-loader-2 inline-block size-5 animate-spin"
              />
            </td>
          </tr>
          <tr v-else-if="filteredUsers.length === 0">
            <td colspan="5" class="px-4 py-8 text-center text-n-slate-11">
              {{ $t('ONLINE_USERS_REPORT.NO_DATA') }}
            </td>
          </tr>
          <template v-else>
            <tr
              v-for="agent in filteredUsers"
              :key="agent.id"
              class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
            >
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <Avatar
                    :name="agent.name"
                    :src="agent.thumbnail"
                    :size="28"
                    rounded-full
                  />
                  <span class="font-medium text-n-slate-12">
                    {{ agent.name }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ agent.email }}
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ roleLabel(agent.role) }}
              </td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="statusClass(agent.availability_status)"
                >
                  {{ statusLabel(agent.availability_status) }}
                </span>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ autoOfflineLabel(agent.auto_offline) }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
